import { useCallback, useEffect, useState } from 'react'
import { CreditPackInfo, getCreditPacks } from '~/billing/services/billingService'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

/**
 * Credit purchase is available for all plans with credit quota:
 * pro, pro_plus, team, enterprise.
 */
interface UseCreditPacksOptions {
  /** Skip fetching when false (e.g. dialog not yet open). Defaults to true. */
  enabled?: boolean
  billingId: string | number | undefined
  /**
   * From useStripeCheckout — opens the Stripe checkout tab synchronously, then
   * runs `getRedirectUrl` (the session-create) and navigates the tab to it.
   */
  startCheckout: (getRedirectUrl: () => Promise<string>) => Promise<boolean>
}

interface UseCreditPacksReturn {
  // Pack data
  creditPacks: CreditPackInfo[]
  pricePerCreditCents: number
  customAmountLimits: { min: number; max: number }

  // Selection state
  selectedFlexPack: string | null
  selectPack: (id: string) => void
  selectCustom: () => void
  customCredits: string
  setCustomCredits: (val: string) => void
  /** Reset selection (e.g. when user picks a subscription plan instead). */
  clearSelection: () => void

  // Derived values
  isCustomFlexPack: boolean
  customCreditsNum: number
  isValidCustomAmount: boolean
  customPriceCents: number
  /** Human-readable summary like "30 credits for $20.00", or null when nothing valid is selected. */
  summaryText: string | null

  // Action
  /** Create a credit-purchase checkout session and open it in a new tab. */
  purchaseCredits: () => Promise<void>
}

/**
 * Manages credit pack selection, pricing, and the purchase API call.
 * Shared between PlanDialog and ZeroCreditsDialog.
 * Credit purchase is available for all plans with credit quota: pro, pro_plus, team, enterprise.
 */
export function useCreditPacks({
  enabled = true,
  billingId,
  startCheckout
}: UseCreditPacksOptions): UseCreditPacksReturn {
  const [creditPacks, setCreditPacks] = useState<CreditPackInfo[]>([])
  const [selectedFlexPack, setSelectedFlexPack] = useState<string | null>(null)
  const [customCredits, setCustomCredits] = useState('')
  const [pricePerCreditCents, setPricePerCreditCents] = useState(65)
  const [customAmountLimits, setCustomAmountLimits] = useState({ min: 30, max: 10_000 })

  // Fetch credit packs when enabled. Credit purchase available for pro, pro_plus, team, enterprise.
  useEffect(() => {
    if (!enabled) return
    getCreditPacks()
      .then(res => {
        setCreditPacks(res.packs)
        setPricePerCreditCents(res.price_per_credit_cents)
        setCustomAmountLimits({ min: res.min_custom_amount, max: res.max_custom_amount })
        if (res.packs.length > 0) {
          setSelectedFlexPack(res.packs[0].id)
        }
      })
      .catch(() => {
        setCreditPacks([])
      })
  }, [enabled])

  // ── Derived values ──
  const isCustomFlexPack = selectedFlexPack === 'custom'
  const customCreditsNum = parseInt(customCredits, 10)
  const isValidCustomAmount =
    !isNaN(customCreditsNum) &&
    customCreditsNum >= customAmountLimits.min &&
    customCreditsNum <= customAmountLimits.max
  const matchingPack = creditPacks.find(p => p.credits === customCreditsNum)
  // Price is computed for any positive integer so the user gets live feedback as they type.
  // Next-button gating still uses isValidCustomAmount, so out-of-range values can't be purchased.
  const customPriceCents =
    !isNaN(customCreditsNum) && customCreditsNum > 0
      ? matchingPack
        ? matchingPack.price_cents
        : customCreditsNum * pricePerCreditCents
      : 0

  const selectedPackInfo = creditPacks.find(p => p.id === selectedFlexPack)
  const summaryText = isCustomFlexPack
    ? isValidCustomAmount
      ? `${customCreditsNum} credits for $${(customPriceCents / 100).toFixed(2)}`
      : null
    : selectedPackInfo
      ? `${selectedPackInfo.credits} credits for ${selectedPackInfo.price_display}`
      : null

  // ── Selection handlers ──
  const selectPack = useCallback((id: string) => {
    setSelectedFlexPack(id)
    setCustomCredits('')
  }, [])

  const selectCustom = useCallback(() => {
    setSelectedFlexPack('custom')
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedFlexPack(null)
    setCustomCredits('')
  }, [])

  // ── Purchase action ──
  const purchaseCredits = useCallback(async () => {
    if (!billingId) {
      BetterToast.error('Billing account not found.')
      return
    }

    try {
      // startCheckout opens the tab synchronously (so it isn't popup-blocked);
      // the session-create runs as the producer, after which the tab navigates.
      const opened = await startCheckout(async () => {
        const successUrl = `${window.location.origin}/checkout-callback?status=success`
        const cancelUrl = `${window.location.origin}/checkout-callback?status=canceled`

        const body = isCustomFlexPack
          ? matchingPack
            ? { pack_type: matchingPack.id, success_url: successUrl, cancel_url: cancelUrl }
            : { custom_amount: customCreditsNum, success_url: successUrl, cancel_url: cancelUrl }
          : {
              pack_type: selectedFlexPack ?? 'starter',
              success_url: successUrl,
              cancel_url: cancelUrl
            }

        const response = await fetchWrapper(`/api/billing/${billingId}/credits/purchase`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            extractApiErrorMessage(
              errorData,
              `Failed to create checkout session: ${response.status}`
            )
          )
        }

        const data = await response.json()
        if (!data.redirect_url) throw new Error('No redirect URL received from checkout session')
        return data.redirect_url
      })
      if (!opened) {
        BetterToast.error('Could not open checkout. Please allow popups and try again.')
      }
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : String(error))
    }
  }, [billingId, isCustomFlexPack, matchingPack, customCreditsNum, selectedFlexPack, startCheckout])

  return {
    creditPacks,
    pricePerCreditCents,
    customAmountLimits,
    selectedFlexPack,
    selectPack,
    selectCustom,
    customCredits,
    setCustomCredits,
    clearSelection,
    isCustomFlexPack,
    customCreditsNum,
    isValidCustomAmount,
    customPriceCents,
    summaryText,
    purchaseCredits
  }
}
